import { spokenAllowance } from "../user/model-allowance-rules.js";
import { proOfferHref } from "../user/pro-offer.js";
import type { Said } from "./hold-to-speak.js";

/**
 * What a 语音答题 Exercise says to a learner, in the words the 拼字 Exercise uses
 * (`./lianxi-pinzi-copy.ts`) for its verdicts, and the words the
 * `english_to_speech` card in 复习 uses (`src/user/fuxi-speech-markup.ts` and
 * `src/user/fuxi-spoken-marked.ts`) for what was heard. **Voice is not
 * pronunciation**, so nothing here says a sound was wrong: a try was
 * understood, or sounded more like another reading.
 *
 * A line with Chinese of its own is three writings, English, 简体 and 繁體,
 * which `./hold-to-speak.ts` draws as `src/user/bilingual.ts` would.
 */

/** The ordinal of a syllable, in English, 简体 and 繁體, from nought. */
const syllableNames: readonly Said[] = [
  ["first", "第一个", "第一個"],
  ["second", "第二个", "第二個"],
  ["third", "第三个", "第三個"],
  ["fourth", "第四个", "第四個"],
];

/** A part of a syllable, in English, 简体 and 繁體. */
const partNames: Readonly<Record<string, Said>> = {
  tone: ["tone", "声调", "聲調"],
  initial: ["initial sound", "声母", "聲母"],
};

export const shuociCopy = {
  /** The line over the words saying where the learner is. */
  progress: (at: number, of: number): string =>
    `Word ${String(at)} of ${String(of)}`,

  right: "Correct 对",
  wrong: "Incorrect 不对. You'll try this word again.",

  /** A first try heard as another reading, with one more to come. */
  tryAgain: (heard: string): Said => [
    `Sounded more like ${heard}. Listen, then try once more.`,
    `听起来更像 ${heard}。听一下，再试一次。`,
    `聽起來更像 ${heard}。聽一下，再試一次。`,
  ],

  /**
   * A first try heard as another word that also means what the page showed,
   * with one more to come. The card's line (`src/user/fuxi-hint-markup.ts`).
   */
  synonym: (rival: string, means: string): Said => [
    `${rival} also means “${means}”. Try another word.`,
    `${rival}也是这个意思。换一个词试试。`,
    `${rival}也是這個意思。換一個詞試試。`,
  ],

  /** A last try heard as another reading, over the verdict. */
  soundedLike: (heard: string): Said => [
    `Sounded more like ${heard}.`,
    `听起来更像 ${heard}。`,
    `聽起來更像 ${heard}。`,
  ],

  /**
   * A pass with one sound off, under the verdict, as the card says it
   * (`heardLine` in `src/user/fuxi-spoken-marked.ts`).
   */
  closeTo: (heard: string): Said => [
    `We understood you. It sounded a little like ${heard}.`,
    `听懂了。发音有点像 ${heard}。`,
    `聽懂了。發音有點像 ${heard}。`,
  ],

  /** A pass, said aloud. */
  understood: ["Understood.", "听懂了。", "聽懂了。"] as Said,

  /**
   * The soft hint a pass may carry, or nothing for a syllable or a part this
   * cannot name.
   */
  hint: (syllable: number, part: string): Said | undefined => {
    const nth = syllableNames[syllable];
    const which = partNames[part];

    return nth === undefined || which === undefined
      ? undefined
      : [
          `You could listen again to the ${which[0]} of the ${nth[0]} syllable.`,
          `可以再听听${nth[1]}音节的${which[1]}。`,
          `可以再聽聽${nth[2]}音節的${which[2]}。`,
        ];
  },

  refused: [
    "You've used all your speaking attempts for today. Type your answer instead.",
    "今天的语音答题次数用完了。请打字回答。",
    "今天的語音答題次數用完了。請打字回答。",
  ] as Said,

  /**
   * What a learner without Pro is told once their taster is used, or once
   * every free learner's is, with Pro offered beside it. Word for word the
   * card's (`freeRefusals` in `src/user/fuxi-speech-markup.ts`), and kept in
   * step with it by hand. A Pro learner is told {@link shuociCopy.refused}.
   */
  freeRefused: {
    spent: [
      `Well done! You've completed your ${String(spokenAllowance.free)} free voice reviews for today. Upgrade to Pro to do even more.`,
      `做得好！你已完成今天的 ${String(spokenAllowance.free)} 次免费语音答题。升级到专业版，就能用语音回答更多题目。`,
      `做得好！你已完成今天的 ${String(spokenAllowance.free)} 次免費語音答題。升級到專業版，就能用語音回答更多題目。`,
    ],
    paused: [
      "Today's free voice reviews have all been used across Chinese Boost. Keep practising by typing, or upgrade to Pro for more voice reviews.",
      "今天全站的免费语音答题次数已用完。你可以继续打字练习，也可以升级到专业版，继续用语音答题。",
      "今天全站的免費語音答題次數已用完。你可以繼續打字練習，也可以升級到專業版，繼續用語音答題。",
    ],
  } as Readonly<Record<"spent" | "paused", Said>>,

  /** The link beside a free refusal, and where it goes. */
  aboutPro: ["About Pro", "了解专业版", "了解專業版"] as Said,
  aboutProHref: proOfferHref("lianxi-speech", "speaking"),

  unavailable: [
    "We couldn't check your recording just now. Try again or type your answer.",
    "暂时无法检查录音。再试一次，或者打字回答。",
    "暫時無法檢查錄音。再試一次，或者打字回答。",
  ] as Said,

  /** The button that plays the word again after a miss. */
  listen: ["Listen", "听", "聽"] as Said,

  /** The finish, with how many were said right first time. */
  finished: (right: number, of: number): string =>
    `Exercise complete. You got ${String(right)} of ${String(of)} words right on your first try.`,

  /** What the finish adds once the words are in 复习, where the cards were not counted. */
  handedOver: "These words are now in Review.",
} as const;
